import { ReactNode, useMemo } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  IcmAnimatedMoney,
  IcmMoneyDisplay,
  splitFormattedMoney,
  IcmTabbedPieChart,
} from "@/icm/common";
import {
  buildStatementBreakdownItems,
  getJobCount,
  getTotalHours,
} from "@/icm/icm-statement-page/utils";
import type {
  EnrichedIcmStatementDetail,
  IcmPayoutBreakdownItem,
  IcmPayoutDisplayType,
} from "@/icm/types";
import {
  buildLineItemsPieChartTabs,
  formatValueByDisplayType,
} from "@/icm/utils";

interface IcmStatementSummaryCardProps {
  statement: EnrichedIcmStatementDetail;
  isSidebarOpen?: boolean;
  hideTotalPay?: boolean;
}

interface SummaryMetricItem {
  key: string;
  label: string;
  displayType: IcmPayoutDisplayType;
  value: number | string | null;
}

const StatBlock = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack align="start" gap={1} className="min-w-0">
    {value}
    <Text variant="label" tone="muted">
      {label}
    </Text>
  </Stack>
);

const SummaryPane = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => <Card className={cn("gap-0 p-4", className)}>{children}</Card>;

export const IcmStatementSummaryCard = ({
  statement,
  isSidebarOpen = false,
  hideTotalPay = false,
}: IcmStatementSummaryCardProps) => {
  const { jobItems, hourlyItems } = useMemo(() => {
    const lineItems = statement.line_items ?? [];

    return {
      jobItems: lineItems.filter((item) => item.job_id != null),
      hourlyItems: lineItems.filter((item) => item.job_id == null),
    };
  }, [statement.line_items]);
  const breakdownItems: IcmPayoutBreakdownItem[] = useMemo(
    () => buildStatementBreakdownItems(statement),
    [statement],
  );
  const pieChartTabs = useMemo(
    () => buildLineItemsPieChartTabs(jobItems),
    [jobItems],
  );
  const hasPayoutMix = pieChartTabs.length > 0;
  const jobCount = useMemo(() => getJobCount(jobItems), [jobItems]);
  const totalHours = useMemo(() => getTotalHours(hourlyItems), [hourlyItems]);
  const totalPayout = parseFloat(statement.total_payout);
  const showSummaryOverview = !hideTotalPay || hasPayoutMix;
  const showStandaloneTotalPayCard = !hideTotalPay;
  const showInlineTotalPay = hideTotalPay || hasPayoutMix;
  const useTwoColumnSummaryLayout =
    !isSidebarOpen && !hideTotalPay && hasPayoutMix;
  const standaloneTotalPayCardClassName = cn(
    "min-h-[220px] items-center justify-center",
    {
      "hidden [@media(min-width:900px)]:flex": hasPayoutMix,
      flex: !hasPayoutMix,
    },
  );
  const inlineTotalPayClassName = cn(
    "text-base leading-[1.1] font-medium tracking-[-0.02em]",
    {
      "[@media(min-width:900px)]:hidden": !hideTotalPay,
    },
  );
  const summaryItems = useMemo(() => {
    const items: SummaryMetricItem[] = [];
    const hasTotalHoursBreakdown = breakdownItems.some(
      (item) => item.key === "total_hours",
    );

    if (jobCount > 0) {
      items.push({
        key: "jobs_worked",
        label: "Jobs worked",
        displayType: "number",
        value: jobCount,
      });
    }

    if (totalHours !== null && !hasTotalHoursBreakdown) {
      items.push({
        key: "total_hours",
        label: "Total hours",
        displayType: "number",
        value: totalHours,
      });
    }

    breakdownItems.forEach((item) => {
      items.push({
        key: item.key,
        label: item.label,
        displayType: item.displayType,
        value: item.value,
      });
    });

    return items;
  }, [breakdownItems, jobCount, totalHours]);

  return (
    <Stack align="start" gap={8} className="w-full">
      {showSummaryOverview && (
        <Stack
          align="start"
          gap={4}
          className={cn("grid grid-cols-1 gap-6", {
            "[@media(min-width:900px)]:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]":
              useTwoColumnSummaryLayout,
          })}
        >
          {showStandaloneTotalPayCard && (
            <SummaryPane className={standaloneTotalPayCardClassName}>
              <Stack gap={1} align="start" className="text-left">
                <IcmAnimatedMoney
                  value={totalPayout}
                  className="text-2xl leading-[1.1] font-medium tracking-[-0.02em] sm:text-3xl"
                  centsClassName="text-base font-normal text-subtle"
                />
                <Text variant="label" tone="muted">
                  Total pay
                </Text>
              </Stack>
            </SummaryPane>
          )}

          {hasPayoutMix && (
            <SummaryPane className="min-w-0">
              <Stack align="start" gap={4}>
                <Stack
                  className="w-full"
                  gap={4}
                  direction="row"
                  justify="between"
                  align="center"
                >
                  <Text variant="label" tone="muted">
                    Payout Mix
                  </Text>
                  {showInlineTotalPay && (
                    <IcmAnimatedMoney
                      value={totalPayout}
                      className={inlineTotalPayClassName}
                      centsClassName="text-xs font-normal text-subtle"
                    />
                  )}
                </Stack>
                <Stack
                  align="start"
                  gap={4}
                  direction="row"
                  justify="center"
                  className="xl:justify-start"
                >
                  <IcmTabbedPieChart
                    tabs={pieChartTabs}
                    className="w-full max-w-[500px]"
                  />
                </Stack>
              </Stack>
            </SummaryPane>
          )}
        </Stack>
      )}

      {summaryItems.length > 0 && (
        <Card className="w-full gap-0 p-6">
          <Stack align="start" gap={4} className="grid grid-cols-2 gap-6">
            {summaryItems.map((item) => {
              const formattedValue = formatValueByDisplayType(
                item.value,
                item.displayType,
              );

              return (
                <Stack align="start" gap={4} key={item.key} className="min-w-0">
                  <StatBlock
                    label={item.label}
                    value={
                      item.displayType === "currency" &&
                      formattedValue !== "-" ? (
                        <IcmMoneyDisplay
                          {...splitFormattedMoney(formattedValue)}
                          className="text-xl leading-[1.2] font-medium tracking-[-0.02em] text-subtle sm:text-2xl"
                          centsClassName="pt-0.5 text-xs font-normal text-subtle"
                        />
                      ) : (
                        <Text
                          variant="display"
                          tone="muted"
                          className="leading-[1.1]"
                        >
                          {formattedValue}
                        </Text>
                      )
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
